import type { IconName } from '@shared/icons';
import type { Permission } from '@services/auth';

/**
 * Single source of truth for routing and navigation.
 *
 * Adding a feature:
 *   1) Append the id to `ROUTE_IDS`.
 *   2) Add a `NavItem` to the right group in `NAV_GROUPS`.
 *   3) Register a lazy import in `src/app/router.tsx` (or it falls back to
 *      <PlaceholderPage/> until the page is built).
 *   4) For a nested page, set `path` ('parent/child') and attach it to the
 *      parent's `children` instead of the group's top-level items.
 *
 * The `as const satisfies` pattern below gives us:
 *   - Literal types (RouteId narrows to the exact union)
 *   - Structural validation that every entry conforms to NavItem / NavGroup
 */
export const ROUTE_IDS = [
  // Workspace
  'dashboard',
  'gettingstarted',
  'settings',
  // Management (Verofy Connect)
  'connectapikeys',
  'connectestate',
  'connectusers',
  'connectcompanies',
  'connectproducts',
  'connectcustomers',
  // Developer
  'apis',
  'sdks',
  'phpsdk',
  'tssdk',
  'pythonsdk',
  'gosdk',
  'dotnetsdk',
  'rubysdk',
  'swiftsdk',
  'kotlinsdk',
  'mcp',
  'mcpdocs',
  'webhooks',
  'settlementwebhooks',
  'vpaymentswebhooks',
  'vpayments',
  'vpaymentsgettingstarted',
  'vpaymentsapiintegration',
  'vpaymentsactivation',
  'vpaymentsvterminalapi',
  'vpaymentspaylinksapi',
  'vpaymentsiframe',
  'vpaymentsiframehomepage',
  'vpaymentsiframevterminal',
  'vpaymentsiframepaylinks',
  'vpaymentsiframerefund',
  'cloudconnect',
  'ccgettingstarted',
  'ccverofymove',
  'ccsdk',
  'ccsdkkotlin',
  'ccsdkkotlininstallation',
  'ccsdkkotlinauthentication',
  'ccsdkkotlininitialization',
  'ccsdkkotlintransactions',
  'ccsdkkotlinrealtimemqtt',
  'ccsdkkotlinerrorhandling',
  'ccsdkdotnet',
  'ccsdkdotnetinstallation',
  'ccsdkdotnetauthentication',
  'ccsdkdotnetinitialization',
  'ccsdkdotnettransactions',
  'ccsdkdotneterrorhandling',
  'ccsdknodejs',
  'ccsdknodejsinstallation',
  'ccsdknodejsauthentication',
  'ccsdknodejsinitialization',
  'ccsdknodejstransactions',
  'ccsdknodejsrealtimemqtt',
  'ccsdknodejserrorhandling',
  'ccsdkphp',
  'ccsdkphpinstallation',
  'ccsdkphpauthentication',
  'ccsdkphpinitialization',
  'ccsdkphptransactions',
  'ccsdkphprealtimemqtt',
  'ccsdkphperrorhandling',
  'ccsdkplatformguides',
  'ccsdkapireference',
  'cctransactions',
  'cctxcreate',
  'cctxresponse',
  'cctxcancel',
  'cctxrefund',
  'cchintstips',
  'softposconnect',
  'spgettingstarted',
  'spappsetup',
  'spsdk',
  'spsdkconfigure',
  'spsdkintegration',
  'spsdkflow',
  'spsdkbestpractices',
  'sptransactions',
  'sptxcreate',
  'sptxresponse',
  'sptxcancel',
  'sptxrefund',
  'sphintstips',
  'spcertifieddevices',
  'sandbox',
  'sandboxwebhooks',
  'playground',
  'dbschema',
  // Intelligence
  'aioverview',
  'aiintegration',
  'aidocschat',
  'aiincident',
  'aiops',
  'aiagents',
  'governance',
  // Operations
  'settlement',
  'terminals',
  'reporting',
  'collections',
  'paylinks',
  'finance',
  // Partner
  'customers',
  'companies',
  'estate',
  'products',
  'residuals',
  'subscriptions',
  'productionreadiness',
  // Infrastructure
  'treasury',
  'tronc',
  'capital',
  'identity',
  'risk',
  'compliance',
  // Support
  'documentation',
  'status',
  'support',
] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

export interface NavItem {
  readonly id: RouteId;
  readonly label: string;
  readonly icon: IconName;
  readonly tag?: string;
  readonly count?: string;
  readonly permission?: Permission;
  readonly description?: string;
  /** Route path override — defaults to `id` when absent (used for nested paths). */
  readonly path?: string;
  /** Nested nav items rendered indented under this item in the sidebar. */
  readonly children?: readonly NavItem[];
}

export interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'grid', permission: 'dashboard.view',
        description: 'Operational overview · partner and merchant signals' },
      { id: 'gettingstarted', label: 'Getting Started', icon: 'rocket',
        permission: 'gettingstarted.view',
        description: 'Provision an account and ship your first integration' },
      { id: 'settings', label: 'Settings', icon: 'bundle', permission: 'settings.view',
        description: 'Workspace configuration' },
    ],
  },
  {
    label: 'Developer',
    items: [
      { id: 'apis', label: 'Family APIs', icon: 'code', count: '13', permission: 'apis.view',
        description: 'REST surface grouped by domain family' },
      { id: 'sdks', label: 'SDKs', icon: 'package', permission: 'sdks.view',
        description: 'Official client libraries',
        children: [
          { id: 'phpsdk', label: 'PHP SDK', icon: 'code',
            path: 'sdks/php', permission: 'sdks.view',
            description: 'PHP SDK integration guide and examples' },
          { id: 'tssdk', label: 'TypeScript SDK', icon: 'code',
            path: 'sdks/typescript', permission: 'sdks.view',
            description: 'TypeScript / Node SDK integration guide and examples' },
          { id: 'pythonsdk', label: 'Python SDK', icon: 'code',
            path: 'sdks/python', permission: 'sdks.view',
            description: 'Python SDK integration guide and examples' },
          { id: 'gosdk', label: 'Go SDK', icon: 'code',
            path: 'sdks/go', permission: 'sdks.view',
            description: 'Go SDK integration guide and examples' },
          { id: 'dotnetsdk', label: '.NET SDK', icon: 'code',
            path: 'sdks/dotnet', permission: 'sdks.view',
            description: '.NET / C# SDK integration guide and examples' },
          { id: 'rubysdk', label: 'Ruby SDK', icon: 'code',
            path: 'sdks/ruby', permission: 'sdks.view',
            description: 'Ruby SDK integration guide and examples' },
          { id: 'swiftsdk', label: 'Swift SDK', icon: 'code',
            path: 'sdks/swift', permission: 'sdks.view',
            description: 'Swift / iOS SDK integration guide and examples' },
          { id: 'kotlinsdk', label: 'Kotlin SDK', icon: 'code',
            path: 'sdks/kotlin', permission: 'sdks.view',
            description: 'Kotlin / Android SDK integration guide and examples' },
        ] },
      { id: 'mcp', label: 'MCP', icon: 'bundle', permission: 'mcp.view',
        description: 'Tool contract for agent integrations',
        children: [
          { id: 'mcpdocs', label: 'Integration Guide', icon: 'book',
            path: 'mcp/guide', permission: 'mcp.view',
            description: 'Install, auth, tools, and the autonomous integration flow' },
        ] },
      { id: 'webhooks', label: 'Webhooks', icon: 'broadcast', permission: 'webhooks.view',
        description: 'Event delivery, retries and signing',
        children: [
          { id: 'settlementwebhooks', label: 'Settlement Webhooks', icon: 'stack',
            path: 'webhooks/settlement', permission: 'webhooks.view',
            description: 'Settlement lifecycle and report delivery webhooks' },
          { id: 'vpaymentswebhooks', label: 'vPayments Webhooks', icon: 'card',
            path: 'webhooks/vpayments', permission: 'webhooks.view',
            description: 'vTerminal & Paylinks transaction webhooks and testing' },
        ] },
      // vPayments — parent has no `path`, so it routes to `/vpayments` (the Overview page).
      // Overview is the parent itself, never a child. API Integration nests its own children,
      // leaving room for iFrame Integration pages to slot in later without a restructure.
      { id: 'vpayments', label: 'vPayments', icon: 'card', permission: 'vpayments.view',
        description: 'Virtual Terminal and PayLinks integration',
        children: [
          { id: 'vpaymentsgettingstarted', label: 'Getting Started', icon: 'rocket',
            path: 'vpayments/getting-started', permission: 'vpayments.view',
            description: 'Integrate via iFrame or the Direct API' },
          { id: 'vpaymentsapiintegration', label: 'API Integration', icon: 'code',
            path: 'vpayments/api-integration', permission: 'vpayments.view',
            description: 'Direct API reference for vTerminal and Paylinks',
            children: [
              { id: 'vpaymentsactivation', label: 'vPayments Activation', icon: 'check',
                path: 'vpayments/activation', permission: 'vpayments.view',
                description: 'Activate vPayment services for a customer location' },
              { id: 'vpaymentsvterminalapi', label: 'vTerminal API', icon: 'cardMachine',
                path: 'vpayments/api-integration/vterminal-api', permission: 'vpayments.view',
                description: 'Virtual Terminal comprehensive guide' },
              { id: 'vpaymentspaylinksapi', label: 'Paylinks API', icon: 'external',
                path: 'vpayments/api-integration/paylinks-api', permission: 'vpayments.view',
                description: 'Paylinks usage scenarios' },
            ] },
          // iFrame Integration — group has no `path`, so it routes to its base and renders the
          // Overview page (the simple integration guide). Children cover each embeddable flow.
          { id: 'vpaymentsiframe', label: 'iFrame Integration', icon: 'bundle',
            path: 'vpayments/iframe-integration', permission: 'vpayments.view',
            description: 'Embed vPayments via iFrame — Virtual Terminal, Paylinks and Refunds',
            children: [
              { id: 'vpaymentsiframehomepage', label: 'Home Screen', icon: 'grid',
                path: 'vpayments/iframe-integration/homepage', permission: 'vpayments.view',
                description: 'Combined Virtual Terminal and Paylinks home screen embed' },
              { id: 'vpaymentsiframevterminal', label: 'vTerminal', icon: 'cardMachine',
                path: 'vpayments/iframe-integration/vterminal-integration', permission: 'vpayments.view',
                description: 'Virtual Terminal iFrame embed' },
              { id: 'vpaymentsiframepaylinks', label: 'Paylinks', icon: 'external',
                path: 'vpayments/iframe-integration/paylinks-integration', permission: 'vpayments.view',
                description: 'Paylinks iFrame embed' },
              { id: 'vpaymentsiframerefund', label: 'Refund', icon: 'stack',
                path: 'vpayments/iframe-integration/refund-integration', permission: 'vpayments.view',
                description: 'Refund and cancel iFrame embed' },
            ] },
        ] },
      // Connect Cloud — parent has no `path`, so it routes to `/cloudconnect` (the Overview).
      // Connect SDK and Transactions are nested groups; SDK languages nest their topics one level
      // deeper (the sidebar renders arbitrary depth).
      { id: 'cloudconnect', label: 'Connect Cloud', icon: 'broadcast', permission: 'cloudconnect.view',
        description: 'Integrate with the Verofy® Move terminal over the cloud',
        children: [
          { id: 'ccgettingstarted', label: 'Getting Started', icon: 'rocket', path: 'cloudconnect/getting-started', permission: 'cloudconnect.view' },
          { id: 'ccverofymove', label: 'Verofy® Move', icon: 'cardMachine', path: 'cloudconnect/verofy-move', permission: 'cloudconnect.view' },
          { id: 'ccsdk', label: 'Connect SDK', icon: 'package', path: 'cloudconnect/connect-sdk', permission: 'cloudconnect.view',
            description: 'Create and observe transactions from your application',
            children: [
              { id: 'ccsdkkotlin', label: 'Kotlin/Java', icon: 'package', path: 'cloudconnect/connect-sdk/kotlin', permission: 'cloudconnect.view',
                children: [
                  { id: 'ccsdkkotlininstallation', label: 'Installation', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/installation', permission: 'cloudconnect.view' },
                  { id: 'ccsdkkotlinauthentication', label: 'Authentication', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/authentication', permission: 'cloudconnect.view' },
                  { id: 'ccsdkkotlininitialization', label: 'Initialization', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/initialization', permission: 'cloudconnect.view' },
                  { id: 'ccsdkkotlintransactions', label: 'Transactions', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/transactions', permission: 'cloudconnect.view' },
                  { id: 'ccsdkkotlinrealtimemqtt', label: 'Realtime / MQTT', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/realtime-mqtt', permission: 'cloudconnect.view' },
                  { id: 'ccsdkkotlinerrorhandling', label: 'Error Handling', icon: 'code', path: 'cloudconnect/connect-sdk/kotlin/error-handling', permission: 'cloudconnect.view' },
                ] },
              { id: 'ccsdkdotnet', label: '.NET', icon: 'package', path: 'cloudconnect/connect-sdk/dotnet', permission: 'cloudconnect.view',
                children: [
                  { id: 'ccsdkdotnetinstallation', label: 'Installation', icon: 'code', path: 'cloudconnect/connect-sdk/dotnet/installation', permission: 'cloudconnect.view' },
                  { id: 'ccsdkdotnetauthentication', label: 'Authentication', icon: 'code', path: 'cloudconnect/connect-sdk/dotnet/authentication', permission: 'cloudconnect.view' },
                  { id: 'ccsdkdotnetinitialization', label: 'Initialization', icon: 'code', path: 'cloudconnect/connect-sdk/dotnet/initialization', permission: 'cloudconnect.view' },
                  { id: 'ccsdkdotnettransactions', label: 'Transactions', icon: 'code', path: 'cloudconnect/connect-sdk/dotnet/transactions', permission: 'cloudconnect.view' },
                  { id: 'ccsdkdotneterrorhandling', label: 'Error Handling', icon: 'code', path: 'cloudconnect/connect-sdk/dotnet/error-handling', permission: 'cloudconnect.view' },
                ] },
              { id: 'ccsdknodejs', label: 'Node.js', icon: 'package', path: 'cloudconnect/connect-sdk/nodejs', permission: 'cloudconnect.view',
                children: [
                  { id: 'ccsdknodejsinstallation', label: 'Installation', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/installation', permission: 'cloudconnect.view' },
                  { id: 'ccsdknodejsauthentication', label: 'Authentication', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/authentication', permission: 'cloudconnect.view' },
                  { id: 'ccsdknodejsinitialization', label: 'Initialization', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/initialization', permission: 'cloudconnect.view' },
                  { id: 'ccsdknodejstransactions', label: 'Transactions', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/transactions', permission: 'cloudconnect.view' },
                  { id: 'ccsdknodejsrealtimemqtt', label: 'Realtime / MQTT', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/realtime-mqtt', permission: 'cloudconnect.view' },
                  { id: 'ccsdknodejserrorhandling', label: 'Error Handling', icon: 'code', path: 'cloudconnect/connect-sdk/nodejs/error-handling', permission: 'cloudconnect.view' },
                ] },
              { id: 'ccsdkphp', label: 'PHP', icon: 'package', path: 'cloudconnect/connect-sdk/php', permission: 'cloudconnect.view',
                children: [
                  { id: 'ccsdkphpinstallation', label: 'Installation', icon: 'code', path: 'cloudconnect/connect-sdk/php/installation', permission: 'cloudconnect.view' },
                  { id: 'ccsdkphpauthentication', label: 'Authentication', icon: 'code', path: 'cloudconnect/connect-sdk/php/authentication', permission: 'cloudconnect.view' },
                  { id: 'ccsdkphpinitialization', label: 'Initialization', icon: 'code', path: 'cloudconnect/connect-sdk/php/initialization', permission: 'cloudconnect.view' },
                  { id: 'ccsdkphptransactions', label: 'Transactions', icon: 'code', path: 'cloudconnect/connect-sdk/php/transactions', permission: 'cloudconnect.view' },
                  { id: 'ccsdkphprealtimemqtt', label: 'Polling', icon: 'code', path: 'cloudconnect/connect-sdk/php/realtime-mqtt', permission: 'cloudconnect.view' },
                  { id: 'ccsdkphperrorhandling', label: 'Error Handling', icon: 'code', path: 'cloudconnect/connect-sdk/php/error-handling', permission: 'cloudconnect.view' },
                ] },
              { id: 'ccsdkplatformguides', label: 'Platform Guides', icon: 'stack', path: 'cloudconnect/connect-sdk/platform-guides', permission: 'cloudconnect.view' },
              { id: 'ccsdkapireference', label: 'API Reference', icon: 'code', path: 'cloudconnect/connect-sdk/api-reference', permission: 'cloudconnect.view' },
            ] },
          { id: 'cctransactions', label: 'Transactions', icon: 'card', path: 'cloudconnect/transactions', permission: 'cloudconnect.view',
            children: [
              { id: 'cctxcreate', label: 'Create Transaction', icon: 'card', path: 'cloudconnect/transactions/create-transaction', permission: 'cloudconnect.view' },
              { id: 'cctxresponse', label: 'Transaction Response', icon: 'card', path: 'cloudconnect/transactions/transaction-response', permission: 'cloudconnect.view' },
              { id: 'cctxcancel', label: 'Cancel Transaction', icon: 'card', path: 'cloudconnect/transactions/cancel-transaction', permission: 'cloudconnect.view' },
              { id: 'cctxrefund', label: 'Refund Transaction', icon: 'card', path: 'cloudconnect/transactions/refund-transaction', permission: 'cloudconnect.view' },
            ] },
          { id: 'cchintstips', label: 'Hints & Tips', icon: 'tips', path: 'cloudconnect/hints-tips', permission: 'cloudconnect.view' },
        ] },
      // SoftPos Connect — parent has no `path`, so it routes to `/softposconnect` (the Overview).
      { id: 'softposconnect', label: 'SoftPos App2App', icon: 'cardMachine', permission: 'softposconnect.view',
        description: 'Accept contactless payments on Android hardware',
        children: [
          { id: 'spgettingstarted', label: 'Getting Started', icon: 'rocket', path: 'softposconnect/getting-started', permission: 'softposconnect.view' },
          { id: 'spappsetup', label: 'App Set-up', icon: 'bundle', path: 'softposconnect/app-set-up', permission: 'softposconnect.view' },
          { id: 'spsdk', label: 'Softpos SDK', icon: 'package', path: 'softposconnect/softpos-sdk', permission: 'softposconnect.view',
            description: 'Configure and integrate the SoftPos Android SDK',
            children: [
              { id: 'spsdkconfigure', label: 'Configure SDK', icon: 'code', path: 'softposconnect/softpos-sdk/configure-sdk', permission: 'softposconnect.view' },
              { id: 'spsdkintegration', label: 'Softpos Integration', icon: 'code', path: 'softposconnect/softpos-sdk/softpos-integration', permission: 'softposconnect.view' },
              { id: 'spsdkflow', label: 'Integration Flow', icon: 'schema', path: 'softposconnect/softpos-sdk/integration-flow', permission: 'softposconnect.view' },
              { id: 'spsdkbestpractices', label: 'Best Practices', icon: 'check', path: 'softposconnect/softpos-sdk/best-practices', permission: 'softposconnect.view' },
            ] },
          { id: 'sptransactions', label: 'Transactions', icon: 'card', path: 'softposconnect/transactions', permission: 'softposconnect.view',
            children: [
              { id: 'sptxcreate', label: 'Create Transaction', icon: 'card', path: 'softposconnect/transactions/create-transaction', permission: 'softposconnect.view' },
              { id: 'sptxresponse', label: 'Transaction Response', icon: 'card', path: 'softposconnect/transactions/transaction-response', permission: 'softposconnect.view' },
              { id: 'sptxcancel', label: 'Cancel Transaction', icon: 'card', path: 'softposconnect/transactions/cancel-transaction', permission: 'softposconnect.view' },
              { id: 'sptxrefund', label: 'Refund Transaction', icon: 'card', path: 'softposconnect/transactions/refund-transaction', permission: 'softposconnect.view' },
            ] },
          { id: 'sphintstips', label: 'Hints & Tips', icon: 'tips', path: 'softposconnect/hints-tips', permission: 'softposconnect.view' },
          { id: 'spcertifieddevices', label: 'Certified Devices', icon: 'terminal', path: 'softposconnect/certified-devices', permission: 'softposconnect.view' },
        ] },
      { id: 'sandbox', label: 'Sandbox', icon: 'flask', permission: 'sandbox.use',
        description: 'Test card numbers, simulated flows',
        children: [
          { id: 'sandboxwebhooks', label: 'Webhooks', icon: 'broadcast',
            path: 'sandbox/webhooks', permission: 'sandbox.use',
            description: 'Trigger sandbox deliveries of settlement and transaction webhooks' },
        ] },
      { id: 'playground', label: 'API Playground', icon: 'bolt', permission: 'apis.view',
        description: 'Compose and run calls against the sandbox' },
      { id: 'dbschema', label: 'Schema Registry', icon: 'schema', permission: 'dbschema.view',
        description: 'Entity relationships, identifiers and lineage' },
    ],
  },
  {
    label: 'Management',
    items: [
      { id: 'connectapikeys', label: 'API Keys', icon: 'vault', path: 'connect/api-keys',
        permission: 'connect.view', description: 'Verofy Connect API keys' },
      { id: 'connectestate', label: 'Estate', icon: 'cardMachine', path: 'connect/estate',
        // Connect API gates devices + softpos to admin + isvAdmin.
        permission: 'connect.estate.view', description: 'Devices and SoftPOS licences' },
      { id: 'connectusers', label: 'Users', icon: 'identity', path: 'connect/users',
        permission: 'connect.view', description: 'Connect users' },
      { id: 'connectcompanies', label: 'Companies', icon: 'building', path: 'connect/companies',
        // Connect API gates the companies list/CRUD to admin only.
        permission: 'connect.companies.view', description: 'Companies' },
      { id: 'connectproducts', label: 'Products', icon: 'package', path: 'connect/products',
        permission: 'connect.view', description: 'POS products' },
      { id: 'connectcustomers', label: 'Customers', icon: 'storefront', path: 'connect/customers',
        // Admins see every customer; ISV admins see only their own (the API scopes the
        // list). Its own permission so developer/installer lose exactly this.
        permission: 'customers.view', description: 'Customers' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'aioverview', label: 'Operational Intelligence', path: 'operational-intelligence', icon: 'chart',
        permission: 'aioverview.view',
        description: 'Decisioning, confidence and exceptions across the platform' },
      { id: 'aiintegration', label: 'Integration Guidance', path: 'integration-guidance', icon: 'rocket',
        permission: 'aiintegration.view',
        description: 'Plan integrations, estimate effort and go-live readiness' },
      { id: 'aidocschat', label: 'Knowledge', path: 'knowledge', icon: 'book', permission: 'aidocschat.view',
        description: 'Search docs and generate SDK, HTTP and MCP examples' },
      { id: 'aiops', label: 'Anomaly Detection', path: 'anomaly-detection', icon: 'pulse', permission: 'aiops.view',
        description: 'Settlement, terminal and webhook anomaly detection' },
      { id: 'aiincident', label: 'Incident Analysis', path: 'incident-analysis', icon: 'bolt', permission: 'aiincident.view',
        description: 'Triage, root-cause and resolution for live incidents' },
      { id: 'aiagents', label: 'Execution Activity', path: 'execution-activity', icon: 'broadcast', permission: 'aiagents.view',
        description: 'Decisions executed, approvals and audit lineage' },
      { id: 'governance', label: 'Governance', icon: 'shield', permission: 'aioverview.view',
        description: 'Approvals, policy enforcement and decision lineage' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'settlement', label: 'Settlement', icon: 'stack', permission: 'settlement.view' },
      { id: 'terminals', label: 'Terminals', icon: 'terminal', count: '1,284',
        permission: 'terminals.view' },
      { id: 'reporting', label: 'Reporting', icon: 'chart', permission: 'reporting.view' },
      { id: 'collections', label: 'Collections', icon: 'pulse', permission: 'collections.view',
        description: 'Merchant-approved intelligent collections' },
      { id: 'paylinks', label: 'PayLinks', icon: 'external', permission: 'paylinks.view',
        description: 'Create and manage hosted payment links' },
      { id: 'finance', label: 'Finance', icon: 'capital', permission: 'finance.view',
        description: 'Embedded finance orchestration' },
    ],
  },
  {
    label: 'Partner',
    items: [
      { id: 'customers', label: 'Customers', icon: 'identity', permission: 'customers.view' },
      { id: 'companies', label: 'Companies', icon: 'package', permission: 'companies.view' },
      { id: 'estate', label: 'Estate & Devices', icon: 'terminal', permission: 'estate.view' },
      { id: 'products', label: 'Products', icon: 'stack', permission: 'products.view' },
      { id: 'residuals', label: 'Residuals & Revenue', icon: 'capital', permission: 'residuals.view' },
      { id: 'subscriptions', label: 'Subscriptions', icon: 'shield',
        permission: 'subscriptions.manage' },
      { id: 'productionreadiness', label: 'Production Readiness', icon: 'check',
        permission: 'productionreadiness.view',
        description: 'Assess whether an ISV is ready to go live' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { id: 'treasury', label: 'Treasury', icon: 'vault', permission: 'treasury.view' },
      { id: 'tronc', label: 'Tronc', icon: 'tips', permission: 'tronc.view' },
      { id: 'capital', label: 'Capital', icon: 'capital', permission: 'capital.view' },
      { id: 'identity', label: 'Identity', icon: 'identity', permission: 'identity.view' },
      { id: 'risk', label: 'Risk', icon: 'shield', permission: 'risk.view' },
      { id: 'compliance', label: 'Compliance', icon: 'lock', permission: 'compliance.view',
        description: 'Operational compliance centre · PCI, KYC, reviews' },
    ],
  },
  {
    label: 'Support',
    items: [
      { id: 'documentation', label: 'Documentation', icon: 'book', permission: 'documentation.view',
        description: 'Guides, references and integration playbooks' },
      { id: 'support', label: 'Support', icon: 'help', permission: 'support.view' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { id: 'status', label: 'Status', icon: 'pulse', permission: 'status.view',
        description: 'Live infrastructure health, uptime and incidents' },
    ],
  },
];

/** Depth-first flatten of nav items including nested children. */
const flattenNavItems = (items: readonly NavItem[]): NavItem[] =>
  items.flatMap((item) => [item, ...(item.children !== undefined ? flattenNavItems(item.children) : [])]);

/** Flat lookup: routeId → NavItem. */
export const NAV_BY_ID: Readonly<Record<RouteId, NavItem>> = Object.freeze(
  NAV_GROUPS.flatMap((g) => flattenNavItems(g.items)).reduce<Record<string, NavItem>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {}) as Record<RouteId, NavItem>,
);

/**
 * Intelligence surfaces hidden when safe mode (VITE_INTELLIGENCE_SAFE_MODE) is on — the
 * intrusive/advanced ones that imply operational execution. Hidden from the sidebar only;
 * routes and code stay intact, so flipping the flag re-exposes them. Safe mode keeps the
 * read-only intelligence set (overview, anomaly, incident, governance, integration, knowledge).
 */
export const SAFE_MODE_HIDDEN_IDS: ReadonlySet<RouteId> = new Set<RouteId>(['aiagents']);

/**
 * Whole sidebar groups hidden when VITE_HIDE_WIP is on (default). Matched by NavGroup.label.
 * Sidebar-only — routes and code stay intact, so flipping the flag re-exposes the sections.
 */
export const HIDE_WIP_HIDDEN_GROUPS: ReadonlySet<string> = new Set<string>([
  'Operations',
  'Partner',
  'Infrastructure',
  'Support',
]);

/**
 * TEMP escape hatch: individual nav items hidden unconditionally from the sidebar as a
 * stop-gap while an issue is worked out. Sidebar-only — the route, page and code stay intact,
 * so removing an id here immediately re-exposes it. Revert by emptying this set.
 * (2026-07-13: emptied — Customers is now properly gated by the Connect role model via
 * the `customers.view` permission, so the 2026-07-10 stop-gap is no longer needed.)
 */
export const TEMP_HIDDEN_NAV_IDS: ReadonlySet<RouteId> = new Set<RouteId>([]);
